export const SERVICES = [
  { id: 'inventory_check_in', name: 'Inventory & Check-In Report', category: 'inventory', short: 'Check-In', description: 'Comprehensive inventory and check-in report documenting the property condition at tenancy start.' },
  { id: 'inventory_check_out', name: 'Inventory & Check-Out Report', category: 'inventory', short: 'Check-Out', description: 'Detailed check-out report comparing end-of-tenancy condition against the original inventory.' },
  { id: 'schedule_of_condition', name: 'Schedule of Condition', category: 'inventory', short: 'Schedule', description: 'Independent condition report for commercial and residential properties.' },
  { id: 'eicr', name: 'EICR (Electrical Safety)', category: 'safety', short: 'EICR', description: 'Electrical Installation Condition Report by NICEIC-registered electricians.' },
  { id: 'gas_safety', name: 'Gas Safety Certificate', category: 'safety', short: 'Gas Safe', description: 'Annual gas safety check (CP12) by Gas Safe registered engineers.' },
  { id: 'epc', name: 'EPC (Energy Performance)', category: 'safety', short: 'EPC', description: 'Energy Performance Certificate by registered assessors.' },
  { id: 'end_of_tenancy_clean', name: 'End-of-Tenancy Cleaning', category: 'cleaning', short: 'Cleaning', description: 'Professional deep cleaning to ensure smooth deposit returns.' },
];

// Prices exclude VAT (added at 20% in calculateQuote). Aligned to the official
// Daley Inventories price list (Knowledge Base, Aug 2026). Sizes studio–5 bed are
// auto-quotable; 6+ bed is priced on application (see MAX_AUTO_BEDROOMS).
export const PRICING = {
  inventory_check_in: { studio: 100, 1: 120, 2: 130, 3: 150, 4: 160, 5: 180 },
  inventory_check_out: { studio: 90, 1: 110, 2: 120, 3: 140, 4: 150, 5: 170 },
  schedule_of_condition: { studio: 100, 1: 110, 2: 120, 3: 140, 4: 150, 5: 170 },
  eicr: { studio: 185, 1: 185, 2: 185, 3: 185, 4: 185, 5: 185 },
  gas_safety: { studio: 90, 1: 90, 2: 90, 3: 90, 4: 90, 5: 90 },
  epc: { studio: 90, 1: 90, 2: 90, 3: 90, 4: 90, 5: 90 },
  end_of_tenancy_clean: { studio: 165, 1: 185, 2: 240, 3: 360, 4: 450, 5: 550 },
};

// Largest property size we auto-quote. 6+ bed is "price on application".
export const MAX_AUTO_BEDROOMS = 5;

// Postcode-area coverage for auto-quoting. Areas outside these lists (or on the
// edge list) are routed to manual review rather than auto-quoted.
export const COVERAGE = {
  core: ['E','W','N','NW','SE','SW','EC','WC'],                        // core London
  suburban: ['KT','TW','CR','BR','SM','HA','EN','IG','RM','UB','DA','WD'], // London belt
  manchester: ['M','BL','OL','SK','WN'],                              // Greater Manchester
  edge: ['TN','RH','CM','SL','RG','AL'],                              // review manually
};

// Extract the leading letters of a UK postcode area (e.g. 'SW1A 1AA' -> 'SW').
export function postcodeArea(postcode) {
  if (!postcode) return '';
  const m = String(postcode).trim().toUpperCase().match(/^[A-Z]{1,2}/);
  return m ? m[0] : '';
}

// Returns { autoQuote: boolean, reason: string } for an enquiry.
export function reviewCheck({ bedrooms, serviceIds = [], postcode = '' } = {}) {
  const reasons = [];
  if (!serviceIds || serviceIds.length === 0) reasons.push('No service selected');
  if (bedrooms === undefined || bedrooms === null || isNaN(bedrooms)) reasons.push('Property size missing');
  else if (bedrooms > MAX_AUTO_BEDROOMS) reasons.push(`${bedrooms}-bed (6+) is price-on-application`);
  const area = postcodeArea(postcode);
  if (postcode) {
    const inCore = COVERAGE.core.includes(area) || COVERAGE.suburban.includes(area) || COVERAGE.manchester.includes(area);
    if (area === 'B') reasons.push('Birmingham — price manually');
    else if (COVERAGE.edge.includes(area)) reasons.push(`Postcode ${area} — price manually (coverage edge)`);
    else if (!inCore) reasons.push(`Postcode ${area} is outside standard coverage`);
  }
  return { autoQuote: reasons.length === 0, reason: reasons.join('; ') };
}

export const SURCHARGES = {
  furnished: 10,
  additional_room: 10,
  sunday_bh: 25,
  after_hours_17: 25,
  after_hours_19: 50,
};

export const BUNDLE_DISCOUNT_RATE = 0.10;
export const VAT_RATE = 0.20;
// Ex VAT. Victor (01/11/2026): express is £60 all inclusive of VAT, i.e. £50 + VAT, within 4 hours.
export const EXPRESS_DELIVERY_FEE = 50;

export const CROSS_SELL = {
  inventory_check_in: ['eicr', 'gas_safety'],
  inventory_check_out: ['eicr', 'end_of_tenancy_clean'],
  schedule_of_condition: ['eicr', 'gas_safety'],
  eicr: ['gas_safety', 'epc'],
  gas_safety: ['eicr', 'epc'],
  epc: ['eicr', 'gas_safety'],
  end_of_tenancy_clean: ['inventory_check_out', 'inventory_check_in'],
};

export const SERVICE_CATEGORIES = {
  inventory: { label: 'Inventory Reports', colour: 'navy' },
  safety: { label: 'Safety Certificates', colour: 'navy' },
  cleaning: { label: 'Cleaning Services', colour: 'navy' },
};

export function getService(serviceId) {
  return SERVICES.find(s => s.id === serviceId);
}

export function getServicePrice(serviceId, bedrooms) {
  const priceTable = PRICING[serviceId];
  if (!priceTable) return 0;
  const key = bedrooms === 0 ? 'studio' : bedrooms;
  return priceTable[key] || priceTable[5] || 0;
}

export function isInventoryService(serviceId) {
  const service = getService(serviceId);
  return service?.category === 'inventory';
}

export function calculateQuote(selectedServiceIds, bedrooms, options = {}) {
  const { furnished = false, additionalRooms = 0, sundayBH = false, expressDelivery = false, preferredTime = '' } = options;
  const hasBundle = selectedServiceIds.length >= 2;

  const lineItems = selectedServiceIds.map(serviceId => {
    const service = getService(serviceId);
    let basePrice = getServicePrice(serviceId, bedrooms);
    let originalPrice = basePrice;
    let discountApplied = false;

    if (hasBundle && isInventoryService(serviceId)) {
      basePrice = basePrice * (1 - BUNDLE_DISCOUNT_RATE);
      discountApplied = true;
    }

    return {
      serviceId,
      serviceName: service?.name || serviceId,
      category: service?.category,
      originalPrice,
      price: Math.round(basePrice),
      discountApplied,
      discountAmount: Math.round(originalPrice - basePrice),
    };
  });

  const surcharges = [];
  const hasInventoryService = selectedServiceIds.some(id => isInventoryService(id));
  if (furnished && hasInventoryService) surcharges.push({ label: 'Furnished property', amount: SURCHARGES.furnished });
  const hasNonSafetyService = selectedServiceIds.some(id => !['eicr', 'gas_safety', 'epc'].includes(id));
  if (additionalRooms > 0 && hasNonSafetyService) surcharges.push({ label: `Additional rooms (${additionalRooms})`, amount: SURCHARGES.additional_room * additionalRooms });
  if (sundayBH) surcharges.push({ label: 'Sunday/Bank Holiday surcharge', amount: SURCHARGES.sunday_bh });
  if (preferredTime === '17:00-19:00') surcharges.push({ label: 'After hours (17:00-19:00)', amount: SURCHARGES.after_hours_17 });
  if (preferredTime === '19:00-21:00') surcharges.push({ label: 'After hours (19:00-21:00)', amount: SURCHARGES.after_hours_19 });
  if (expressDelivery) surcharges.push({ label: 'Express Delivery (4-hour turnaround)', amount: EXPRESS_DELIVERY_FEE, type: 'express' });

  const servicesTotal = lineItems.reduce((sum, item) => sum + item.price, 0);
  const surchargeTotal = surcharges.reduce((sum, s) => sum + s.amount, 0);
  const totalDiscount = lineItems.reduce((sum, item) => sum + item.discountAmount, 0);
  const subtotal = servicesTotal + surchargeTotal;
  const vatAmount = Math.round(subtotal * VAT_RATE * 100) / 100;
  const grandTotalWithVAT = Math.round((subtotal + vatAmount) * 100) / 100;

  return {
    lineItems,
    surcharges,
    servicesTotal,
    surchargeTotal,
    totalDiscount,
    grandTotal: subtotal,
    subtotal,
    vatAmount,
    grandTotalWithVAT,
    hasBundleDiscount: totalDiscount > 0,
  };
}

export function getCrossSellSuggestions(selectedServiceIds) {
  const suggestions = [];
  const seen = new Set(selectedServiceIds);
  for (const id of selectedServiceIds) {
    const cs = CROSS_SELL[id] || [];
    for (const s of cs) {
      if (!seen.has(s) && !suggestions.find(x => x.serviceId === s)) {
        suggestions.push({ serviceId: s, recommendedFrom: id });
      }
    }
  }
  return suggestions.slice(0, 3);
}

export function formatPrice(amount) {
  return '\u00A3' + amount.toFixed(2);
}

export function getStartingPrice(serviceId) {
  const prices = PRICING[serviceId];
  if (!prices) return 0;
  return Math.min(...Object.values(prices));
}